import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { Router } from "@angular/router";
import { AlertService } from "../Services/notifications/alert.service";
import { AuthService } from "../Services/auth";
import { EncryptionService } from "../Services/encryption";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private alertService: AlertService,
    private router: Router,
    private authService: AuthService,
    private encryptionService: EncryptionService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        let errorMessage = "";
        console.log("ErrorInterceptor caught an error:", error);

        try {
          const body = error && error.error ? error.error : null;
          if (body && body.response) {
            try {
              const decrypted = this.encryptionService.decrypt(body.response);
              const decryptedMsg =
                decrypted && typeof decrypted === "object"
                  ? decrypted.message || decrypted.msg || decrypted.error
                  : decrypted;
              if (decryptedMsg) {
                errorMessage = decryptedMsg;
              }
              (error as any).error = decrypted;
            } catch (decErr) {
              console.debug("Error decryption failed in interceptor", decErr);
            }
          }
        } catch (e) {
          // ignore any failures during decrypt attempt and continue with normal flow
        }

        if (!errorMessage) {
          if (error.error instanceof ErrorEvent) {
            errorMessage = `Client Error: ${error.error.message}`;
          } else {
            console.log("Client-side error detected:", error.status, error);
            switch (error.status) {
              case 400:
                errorMessage =
                  error.error?.message ||
                  "Bad Request: Please check your input data.";
                break;
              case 401:
                errorMessage =
                  error.error?.message || "Unauthorized: Please log in again.";
                break;
              case 403:
                errorMessage =
                  error.error?.message ||
                  "Forbidden: You do not have permission to access this resource.";
                break;
              case 404:
                errorMessage =
                  error.error?.message ||
                  "Not Found: The requested resource was not found.";
                break;
              case 406:
                errorMessage =
                  error.error?.message ||
                  "Not Acceptable: The requested resource is not acceptable.";
                break;
              case 500:
                errorMessage =
                  error.error?.message ||
                  "Internal Server Error: Please try again later.";
                break;
              case 503:
                errorMessage =
                  error.error?.message ||
                  "Service Unavailable: Please try again later.";
                break;
              default:
                errorMessage =
                  error.error?.message ||
                  `Server Error: ${error.status} - ${error.statusText}`;
            }
          }
        }

        const isNetworkFailure = !error.status || error.status === 0;
        const isServerError = error.status >= 500 && error.status < 600;

        if (isNetworkFailure || isServerError) {
          try {
            if (error.status !== 401) {
              this.router.navigate(["/server-unreachable"]);
            } else {
              // If we received a 401, the user's session is already invalid on the server.
              // Avoid attempting another backend logout (which will also return 401).
              // Perform local cleanup immediately.
              try {
                this.authService.logoutLocal(
                  true,
                  "Session expired. You have been logged out.",
                );
              } catch (e) {
                /* ignore */
              }
            }
          } catch (navErr) {
            // ignore
          }
        }

        // Show error message to user (except for 401 which is handled by AuthInterceptor)
        if (error.status !== 401 && !isNetworkFailure) {
          this.showErrorMessage(errorMessage);
        }

        // Log error to console for debugging
        console.error("HTTP Error:", {
          status: error.status,
          statusText: error.statusText,
          message: errorMessage,
          error: error.error,
          url: error.url,
        });

        return throwError(() => ({
          ...error,
          userMessage: errorMessage,
        }));
      }),
    );
  }

  private showErrorMessage(message: string): void {
    console.log("Showing error message to user:", message);
    if (!message.includes("session")) {
      this.alertService.show(message, { type: "error", duration: 5000 });
    }
  }
}
